/**
 * useReservationPermissions — encapsulates reservation permission resolution.
 *
 * Both /reservations and /media-units/:id pages need the same permission set.
 * Formerly 6 granular codes; collapsed to reservation.manage after task 1.1.
 *
 * Returns a permissions object compatible with ReservationListViewPermissions.
 *
 * Fail-closed while loading: privileged reservation controls (upgrade, cancel,
 * force-release, override status, block availability) stay hidden until the
 * permission context resolves, instead of flashing open (CU-86cb4wjwm).
 */
import { useCurrentUser } from '../../plugs/current-user';

/** Permissions still loading (null) fail closed here, unlike `useHasPermission`, so privileged controls never flash open. */
function useHasPermissionClosed(key: string): boolean {
  const { permissions } = useCurrentUser();
  return permissions !== null && permissions.includes(key);
}

export interface ReservationPermissions {
  canUpgradeHard: boolean;
  canCloseSale: boolean;
  canCancel: boolean;
  canUpdateDates: boolean;
  canForceRelease: boolean;
  canOverrideStatus: boolean;
  isAdmin: boolean;
  canManageWaitlist: boolean;
  /** Holder of media_unit.block_availability — sees the "Zablokuj dostępność" action. */
  canBlockAvailability: boolean;
  /**
   * Holder of reservation.extend — sees "Edytuj termin wygaśnięcia" on an
   * eligible SOFT/HARD row (CU-86cb7qgc9). A newly-wired, narrow permission —
   * distinct from `reservation.manage`/`media_unit.update_availability`, per
   * the action-permissions spec's "reservation.extend authorizes only manual
   * effective-expiry editing" requirement.
   */
  canExtendExpiry: boolean;
  /**
   * True while the permission context has not resolved yet. Every boolean
   * above is provisionally false during this window (fail-closed render
   * gating) — callers that derive a one-shot side effect (e.g. a URL
   * rewrite) from "lacks permission" MUST also check this flag first, or
   * they will misfire for a user who actually holds the permission once it
   * resolves (CU-86cb4wjwm).
   */
  isLoading: boolean;
}

/**
 * computeForceFlag — CU-86can6mmu: force=true only for other users'
 * reservations, and only when the caller holds reservation.force_release.
 * Shared by /reservations and /media-units/:id action-confirm handlers so
 * both stay in sync with the row-action gating (isOwn || canForceRelease).
 */
export function computeForceFlag(
  reservation: { holderUserId: number },
  currentUserId: number,
  canForceRelease: boolean,
): boolean {
  const isOwn = reservation.holderUserId === currentUserId;
  return !isOwn && canForceRelease;
}

/** Permission codes behind `ReservationPermissions`; shared with test providers that stand in for the current-user plug. */
export const RESERVATION_PERMISSION_CODES = {
  manage: 'reservation.manage',
  forceRelease: 'reservation.force_release',
  manageWaitlist: 'reservation.manage_waitlist',
  overrideStatus: 'media_unit.update_availability',
  blockAvailability: 'media_unit.block_availability',
  extendExpiry: 'reservation.extend',
} as const;

export function useReservationPermissions(): ReservationPermissions {
  const { permissions } = useCurrentUser();
  const canManage = useHasPermissionClosed(RESERVATION_PERMISSION_CODES.manage);
  const canForceRelease = useHasPermissionClosed(RESERVATION_PERMISSION_CODES.forceRelease);
  const canManageWaitlist = useHasPermissionClosed(RESERVATION_PERMISSION_CODES.manageWaitlist);
  const canOverrideStatus = useHasPermissionClosed(RESERVATION_PERMISSION_CODES.overrideStatus);
  const canBlockAvailability = useHasPermissionClosed(
    RESERVATION_PERMISSION_CODES.blockAvailability,
  );
  const canExtendExpiry = useHasPermissionClosed(RESERVATION_PERMISSION_CODES.extendExpiry);

  return {
    canUpgradeHard: canManage,
    canCloseSale: canManage,
    canCancel: canManage,
    canUpdateDates: canManage,
    canForceRelease,
    canOverrideStatus,
    isAdmin: canManage,
    canManageWaitlist,
    canBlockAvailability,
    canExtendExpiry,
    isLoading: permissions === null,
  };
}
